import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Building2,
  Plug,
  Users,
  Activity,
  Copy,
  Plus,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatCard } from '@/components/shared/StatCard';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface AdminOverviewTabProps {
  readonly onTabChange: (tab: string) => void;
}

const STATUS_STYLE: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-500/15 text-green-400' },
  suspended: { label: 'Suspended', className: 'bg-red-500/15 text-red-400' },
  provisioning: { label: 'Provisioning', className: 'bg-amber-500/15 text-amber-400' },
};

function CopyableId({ label, value }: { readonly label: string; readonly value: string }) {
  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">{label}:</span>
      <code className="font-mono text-xs text-foreground/80">{value}</code>
      <button
        type="button"
        onClick={handleCopy}
        className="text-muted-foreground hover:text-foreground"
      >
        <Copy className="h-3 w-3" />
      </button>
    </div>
  );
}

export function AdminOverviewTab({ onTabChange }: AdminOverviewTabProps) {
  const { data: tenant, isLoading } = useQuery({
    queryKey: ['tenant'],
    queryFn: () => api.admin.tenant.get(),
    staleTime: 300_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-6 pt-6">
        <div className="h-40 animate-pulse rounded-lg bg-muted" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (!tenant) return null;

  const statusStyle = STATUS_STYLE[tenant.status] ?? STATUS_STYLE.active;

  return (
    <div className="space-y-6 pt-6">
      {/* Tenant Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-foreground">
                  {tenant.organizationName}
                </h2>
                <span
                  className={cn(
                    'inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold',
                    statusStyle.className,
                  )}
                >
                  {statusStyle.label}
                </span>
              </div>
              <div className="mt-3 space-y-1.5">
                <CopyableId label="Tenant ID" value={tenant.tenantId} />
                <CopyableId label="Customer ID" value={tenant.customerId} />
              </div>
            </div>
            <div className="text-right text-sm text-muted-foreground">
              <p>
                Activated:{' '}
                {tenant.activatedAt
                  ? new Date(tenant.activatedAt).toLocaleDateString()
                  : 'Pending'}
              </p>
              <p>
                Created: {new Date(tenant.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Active Departments"
          value={tenant.activeDepartments.length}
          icon={Building2}
        />
        <StatCard
          label="Connectors"
          value={tenant.connectorCount}
          icon={Plug}
        />
        <StatCard
          label="Reviewers"
          value={tenant.reviewerCount}
          icon={Users}
        />
        <StatCard
          label="Status"
          value={tenant.status}
          icon={Activity}
          colorOverride={
            tenant.status === 'active'
              ? 'text-green-400'
              : tenant.status === 'suspended'
                ? 'text-red-400'
                : 'text-amber-400'
          }
        />
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-foreground">
          Quick Actions
        </h3>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={() => onTabChange('connectors')}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add Connector
          </Button>
          <Button
            variant="outline"
            onClick={() => onTabChange('reviewers')}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add Reviewer
          </Button>
          <Button
            variant="outline"
            onClick={() => onTabChange('departments')}
          >
            <ArrowRight className="mr-1.5 h-3.5 w-3.5" />
            Manage Departments
          </Button>
        </div>
      </div>
    </div>
  );
}
